import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';

const CompanyChat = () => {
  const { companySlug, sessionId } = useParams();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionInfo, setSessionInfo] = useState(null);
  const messagesEndRef = useRef(null);

  // Funktion um Nachrichteninhalt zu formatieren
  const formatMessageContent = (content) => {
    if (!content) return '';
    
    // Escape HTML-Zeichen
    let formatted = content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    
    // Markdown-ähnliche Formatierung
    // Fettgedruckt: **text** oder __text__
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    formatted = formatted.replace(/__(.*?)__/g, '<strong>$1</strong>');
    
    // Kursiv: *text* oder _text_
    formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');
    formatted = formatted.replace(/_(.*?)_/g, '<em>$1</em>');
    
    // Code: `code`
    formatted = formatted.replace(/`([^`]+)`/g, '<code>$1</code>');
    
    // Code-Blöcke: ```code```
    formatted = formatted.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>');
    
    // Überschriften: # ## ###
    formatted = formatted.replace(/^### (.*$)/gm, '<h3>$1</h3>');
    formatted = formatted.replace(/^## (.*$)/gm, '<h2>$1</h2>');
    formatted = formatted.replace(/^# (.*$)/gm, '<h1>$1</h1>');
    
    // Listen: - item oder * item
    formatted = formatted.replace(/^[\s]*[-*] (.*$)/gm, '<li>$1</li>');
    formatted = formatted.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');
    
    // Nummerierte Listen: 1. item
    formatted = formatted.replace(/^[\s]*\d+\. (.*$)/gm, '<li>$1</li>');
    
    // Links: [text](url)
    formatted = formatted.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    
    // Zeilenumbrüche
    formatted = formatted.replace(/\n/g, '<br>');
    
    return formatted;
  };

  useEffect(() => {
    loadSessionInfo();
    loadMessages();
  }, [companySlug, sessionId]);

  const loadSessionInfo = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/company-sessions/${companySlug}`);
      if (response.ok) {
        const sessions = await response.json();
        const session = sessions.find(s => s.id === sessionId);
        if (session) {
          setSessionInfo(session);
        }
      }
    } catch (error) {
      console.error('Fehler beim Laden der Session-Info:', error);
    }
  };

  const loadMessages = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/company-sessions/${companySlug}/${sessionId}/messages`);
      if (response.ok) {
        const messages = await response.json();
        const formattedMessages = messages.map(msg => ({
          id: `${msg.timestamp}-${Math.random()}`,
          type: msg.role === 'user' ? 'user' : 'bot',
          content: msg.content,
          timestamp: msg.timestamp
        }));
        setMessages(formattedMessages);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Nachrichten:', error);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || loading) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      type: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/company-chat/${companySlug}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          message: inputMessage
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const botMessage = {
          id: `bot-${Date.now()}`,
          type: 'bot',
          content: data.response,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, botMessage]);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Fehler beim Senden der Nachricht');
      }
    } catch (error) {
      console.error('Fehler beim Senden:', error);
      setError(error.message);
      const errorMessage = {
        id: `error-${Date.now()}`,
        type: 'bot',
        content: `Entschuldigung, es ist ein Fehler aufgetreten: ${error.message}`,
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const copyMessage = (content) => {
    navigator.clipboard.writeText(content).then(() => {
      // Optional: Toast-Benachrichtigung
    });
  };

  const clearChat = () => {
    setMessages([]);
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <Box sx={{ 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: '#f5f5f5'
    }}>
      {/* Header */}
      <Paper sx={{ 
        p: { xs: 1.5, sm: 2 }, 
        borderRadius: 0,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        bgcolor: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flex: 1, minWidth: 0 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 } }}>
              <BotIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                fontSize: { xs: '1rem', sm: '1.25rem' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {sessionInfo?.company_name || 'KI-Assistent'}
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                gap: { xs: 0.5, sm: 1 }, 
                alignItems: 'center',
                flexWrap: 'wrap',
                mt: 0.5
              }}>
                <Chip
                  label={companySlug}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  display: { xs: 'none', sm: 'block' }
                }}>
                  Session: {sessionId}
                </Typography>
              </Box>
            </Box>
          </Box>
          <IconButton 
            onClick={clearChat} 
            title="Chat leeren"
            sx={{ 
              flexShrink: 0,
              p: { xs: 0.5, sm: 1 }
            }}
          >
            <RefreshIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
          </IconButton>
        </Box>
      </Paper>

      {/* Messages */}
      <Box sx={{ 
        flexGrow: 1, 
        overflow: 'auto', 
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}>
        {messages.map((message) => (
          <Box
            key={message.id}
            sx={{
              display: 'flex',
              justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
              alignItems: 'flex-start',
              gap: 1,
            }}
          >
            {message.type === 'bot' && (
              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                <BotIcon fontSize="small" />
              </Avatar>
            )}
            
            <Paper
              sx={{
                p: 2,
                maxWidth: '70%',
                bgcolor: message.type === 'user' ? 'primary.main' : 'white',
                color: message.type === 'user' ? 'white' : 'text.primary',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                border: message.isError ? '1px solid #f44336' : 'none',
              }}
            >
              <Typography 
                variant="body1" 
                sx={{ 
                  whiteSpace: 'pre-wrap',
                  '& strong': { fontWeight: 'bold' },
                  '& em': { fontStyle: 'italic' },
                  '& code': { 
                    backgroundColor: 'rgba(0,0,0,0.1)', 
                    padding: '2px 4px', 
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '0.9em'
                  },
                  '& pre': {
                    backgroundColor: 'rgba(0,0,0,0.05)',
                    padding: '8px',
                    borderRadius: '4px',
                    overflow: 'auto',
                    fontFamily: 'monospace',
                    fontSize: '0.9em'
                  },
                  '& ul, & ol': {
                    paddingLeft: '20px',
                    margin: '8px 0'
                  },
                  '& li': {
                    margin: '4px 0'
                  },
                  '& h1, & h2, & h3, & h4, & h5, & h6': {
                    fontWeight: 'bold',
                    margin: '12px 0 8px 0'
                  },
                  '& h1': { fontSize: '1.5em' },
                  '& h2': { fontSize: '1.3em' },
                  '& h3': { fontSize: '1.1em' },
                  '& blockquote': {
                    borderLeft: '4px solid #ccc',
                    paddingLeft: '12px',
                    margin: '8px 0',
                    fontStyle: 'italic'
                  },
                  '& table': {
                    borderCollapse: 'collapse',
                    width: '100%',
                    margin: '8px 0'
                  },
                  '& th, & td': {
                    border: '1px solid #ddd',
                    padding: '8px',
                    textAlign: 'left'
                  },
                  '& th': {
                    backgroundColor: 'rgba(0,0,0,0.05)',
                    fontWeight: 'bold'
                  }
                }}
                dangerouslySetInnerHTML={{ 
                  __html: formatMessageContent(message.content) 
                }}
              />
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mt: 1,
                gap: 1
              }}>
                <Typography variant="caption" sx={{ 
                  opacity: 0.7,
                  color: message.type === 'user' ? 'white' : 'text.secondary'
                }}>
                  {new Date(message.timestamp).toLocaleTimeString('de-DE', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>
                {message.type === 'bot' && (
                  <IconButton
                    size="small"
                    onClick={() => copyMessage(message.content)}
                    sx={{ 
                      color: message.type === 'user' ? 'white' : 'text.secondary',
                      opacity: 0.7,
                      '&:hover': { opacity: 1 }
                    }}
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Paper>

            {message.type === 'user' && (
              <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>
                <PersonIcon fontSize="small" />
              </Avatar>
            )}
          </Box>
        ))}
        
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
              <BotIcon fontSize="small" />
            </Avatar>
            <Paper sx={{ p: 2, bgcolor: 'white' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Denkt nach...
                </Typography>
              </Box>
            </Paper>
          </Box>
        )}
        
        <div ref={messagesEndRef} />
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          onClose={() => setError(null)}
          sx={{ m: 2 }}
        >
          {error}
        </Alert>
      )}

      {/* Input */}
      <Paper sx={{ 
        p: 2, 
        borderRadius: 0,
        boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
        bgcolor: 'white'
      }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Schreiben Sie Ihre Nachricht..."
            disabled={loading}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: { xs: '16px', sm: '1rem' }, // Verhindert Zoom auf iOS
              }
            }}
          />
          <IconButton
            onClick={sendMessage}
            disabled={!inputMessage.trim() || loading}
            color="primary"
            sx={{ 
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' },
              '&:disabled': { bgcolor: 'grey.300' }
            }}
          >
            <SendIcon />
          </IconButton>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Drücken Sie Enter zum Senden, Shift+Enter für neue Zeile
        </Typography>
      </Paper>
    </Box>
  );
};

export default CompanyChat;
